import React, { useEffect, useState, useMemo } from 'react';
import { StyleSheet, Platform, View, Pressable, ScrollView } from 'react-native';
import { KeyboardAwareScrollView } from 'react-native-keyboard-controller';
import { useTheme } from '@/context/ThemeContext';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import NavigationBarButton from '@/components/Navigation/NavigationBarButton';
import Button from '@/components/Buttons/Button';
import useResponsiveness from '@/hooks/useResponsiveness';
import useComposerKeyboardHeight from '@/hooks/useComposerKeyboardHeight';
import useModalFocusVisibility from '@/hooks/useModalFocusVisibility';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

export default function ModalLayout({ 
  onClose,
  title,
  titleBorder = true,
  lowerActionsBorder = true,
  hideCloseButton = false,
  children,
  maxHeight, // kept for API compat; no longer used internally
  contentStyle,
  contentContainerStyle,
  containerStyle,
  headerActions,
  footerContent,
  lowerActions = [],
  inlineLowerActions = false,
  onCloseIcon = 'close',
  closeAccessibilityLabel = 'Close',
  background = 'primary',
  onLowerActionsLayout, // New prop to handle layout of lower actions
  refreshControl,
  /** When false, body is a plain View (flex) instead of ScrollView — use for nested FlashList/FlatList so onEndReached works. */
  scrollBody = true,
}) {
  const { colors } = useTheme();
  const { isXS } = useResponsiveness();
  const insets = useSafeAreaInsets();
  const [isNearTop, setIsNearTop] = useState(true);
  const hasActions = Array.isArray(lowerActions) && lowerActions.length > 0;
  const isNative = Platform.OS !== 'web';
  const isMobileOrXS = isXS && Platform.OS !== 'macos';
  const isMacOS = Platform.OS === 'macos';

  const composerKeyboardHeight = useComposerKeyboardHeight();
  // Lift the footer and shrink the body on both phones. KeyboardAvoidingView
  // cannot see ModalRoot's transform, so iOS was leaving name/instructions
  // under the keyboard while Android already inset correctly.
  const keyboardHeight = isNative && !isMacOS ? composerKeyboardHeight : 0;
  const usesAwareScroll = isNative && !isMacOS && scrollBody;

  const memoizedLowerActions = React.useMemo(() => lowerActions, [lowerActions]);
  const isWebMobile = Platform.OS === 'web' && isXS;
  const scrollRef = React.useRef(null);
  useModalFocusVisibility(scrollRef, isWebMobile && scrollBody);
  const isInlineLowerActions = inlineLowerActions && (lowerActions.length >= 2 || !isXS);

  const getActionsHeight = (actionsCount) => {
    if (actionsCount === 0) return 0;
    const buttonHeight = isXS ? 48 : 44;
    const gap = 8;
    const paddingTop = lowerActionsBorder ? (isXS ? 12 : 15) : 0;
    const paddingBottomBase = isNative ? 10 : (isXS ? 12 : 15);
    const rows = isInlineLowerActions ? 1 : actionsCount;
    return paddingTop + (buttonHeight + gap) * rows - gap + paddingBottomBase;
  };

  useEffect(() => {
    onLowerActionsLayout?.(memoizedLowerActions);
  }, [memoizedLowerActions, onLowerActionsLayout]);

  const styles = useMemo(() => StyleSheet.create({
    scrollContent: {
      flexGrow: 1,
      flexShrink: 0,
    },
    container: {
      flex: 1,
      // Required for the body ScrollView to shrink inside a fixed-height modal
      // shell (esp. mobile web). Without this, flex content overflows and is
      // clipped by the shell's overflow:hidden instead of scrolling.
      minHeight: 0,
      display: 'flex',
      flexDirection: 'column',
      width: '100%',
      position: 'relative',
      backgroundColor: background === 'block' ? colors.solid_color_block : colors.solid_color_primary,
      // Keep the native footer in the column so the body gets all remaining
      // height. Inset the whole column while the phone keyboard is visible.
      ...(isNative && isMobileOrXS && { paddingBottom: keyboardHeight }),
    },
    header: {
      flexShrink: 0,
      backgroundColor: background === 'block' ? colors.solid_color_block : colors.solid_color_primary,
      borderBottomWidth: titleBorder ? (isNearTop ? (isXS ? 0 : 0.5) : 1) : 0,
      borderColor: titleBorder ? colors.border_color_secondary : 'transparent',
      paddingHorizontal: isXS ? 8 : 10,
      paddingVertical: isXS ? 12 : 10,
      minHeight: isXS ? 48 : 42,
      justifyContent: 'center',
    },
    headerContent: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      minHeight: isXS ? 44 : 42,
    },
    leftContainer: {
      minWidth: 40,
      paddingLeft: isXS ? 5 : 0,
      alignItems: 'flex-start',
    },
    title: {
      textAlign: 'center',
      flex: 1,
      minWidth: 0,
    },
    headerActionContainer: {
      minWidth: 40,
      paddingRight: isXS ? 5 : 0,
      alignItems: 'flex-end',
      justifyContent: 'flex-end',
      flexDirection: 'row',
      gap: 4,
    },
    contentNoScroll: {
      flex: 1,
      minHeight: 0,
      flexBasis: 0,
    },
    content: {
      flex: 1,
      flexBasis: 0,
      minHeight: 0,
      ...(isWebMobile && hasActions && {
        marginBottom: getActionsHeight(lowerActions.length),
      }),
    },
    actions: {
      flexShrink: 0,
      padding: 15,
      paddingTop: lowerActionsBorder ? (isXS ? 12 : 15) : 0,
      paddingBottom: isNative ? (keyboardHeight > 0 ? 10 : insets.bottom + 10) : (isXS ? 12 : 15),
      minHeight: isMacOS ? 72 : isXS ? 50 : 55,
      borderTopWidth: lowerActionsBorder ? 1 : 0,
      borderTopColor: lowerActionsBorder ? colors.border_color_secondary : 'transparent',
      backgroundColor: background === 'block' ? colors.solid_color_block : colors.solid_color_primary,
      ...(isWebMobile && {
        position: 'absolute',
        bottom: 0,
        left: 0,
        right: 0,
      }),
      gap: 8,
    },
    actionsInline: {
      flexDirection: 'row',
      justifyContent: 'flex-end',
      gap: 12,
    },
    action: {
      flexShrink: 0,
      minWidth: 0,
    },
    actionInline: {
      flex: 1,
      minWidth: 0,
    },
  }), [colors, background, titleBorder, isNearTop, isXS, isNative, isMobileOrXS, isWebMobile, hasActions, lowerActions.length, lowerActionsBorder, insets.bottom, keyboardHeight, isInlineLowerActions]);

  const content = (
    <>
        {
          (hideCloseButton && !title && !headerActions) ? null : (
          <View style={[styles.header, { borderBottomColor: colors.border_color }]}>
            <View style={styles.headerContent}>
              <View style={styles.leftContainer}>
                {
                  !hideCloseButton && (
                    isNative ? (
                      <Pressable
                        accessibilityRole="button"
                        accessibilityLabel={closeAccessibilityLabel}
                        onPress={onClose}
                        hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
                        style={({ pressed }) => ({ padding: 8, borderRadius: 8, opacity: pressed ? 0.6 : 1 })}
                      >
                        <Icon name={onCloseIcon === 'close' ? 'xmark' : onCloseIcon} size={18} color={colors.text_color_primary} />
                      </Pressable>
                    ) : (
                      <NavigationBarButton
                        accessibilityLabel={closeAccessibilityLabel}
                        icon={onCloseIcon}
                        onPress={onClose}
                        tooltip='Close'
                        tooltipShortcut='Esc'
                      />
                    )
                  )
                }
              </View>
              <Text 
                size='l' 
                weight='medium' 
                style={styles.title}
                numberOfLines={2}
              >
                {title}
              </Text>
              <View style={styles.headerActionContainer}>
                {headerActions && headerActions.map((action, index) => (
                  <NavigationBarButton 
                    key={index}
                    icon={action.icon}
                    label={action.label}
                    solidIcon={action.solidIcon}
                    title={action.title}
                    onPress={action.onPress}
                    isDanger={action.isDanger}
                    tooltip={action.tooltip}
                    variant={action.variant}
                  />
                ))}
              </View>
            </View>
          </View>
        )}

        {scrollBody ? (
          usesAwareScroll ? (
            <KeyboardAwareScrollView
              ref={scrollRef}
              style={[styles.content, contentStyle]}
              contentContainerStyle={[styles.scrollContent, contentContainerStyle]}
              onScroll={(e) => setIsNearTop(e.nativeEvent.contentOffset.y < 10)}
              scrollEventThrottle={16}
              showsVerticalScrollIndicator={isMacOS}
              keyboardShouldPersistTaps="handled"
              keyboardDismissMode="on-drag"
              bottomOffset={hasActions ? getActionsHeight(lowerActions.length) + 16 : 24}
              refreshControl={refreshControl}
            >
              {children}
            </KeyboardAwareScrollView>
          ) : (
            <ScrollView
              ref={scrollRef}
              style={[styles.content, contentStyle]}
              contentContainerStyle={[styles.scrollContent, contentContainerStyle]}
              onScroll={(e) => setIsNearTop(e.nativeEvent.contentOffset.y < 10)}
              scrollEventThrottle={16}
              showsVerticalScrollIndicator={isMacOS}
              keyboardShouldPersistTaps={isWebMobile ? "always" : "handled"}
              keyboardDismissMode={isWebMobile ? "none" : "on-drag"}
              refreshControl={refreshControl}
            >
              {children}
            </ScrollView>
          )
        ) : (
          <View style={[styles.content, styles.contentNoScroll, contentStyle]}>
            {children}
          </View>
        )}

        {footerContent && <View style={{ flexShrink: 0 }} testID="modal-fixed-footer">{footerContent}</View>}

        {lowerActions.length > 0 && (
          <View style={[styles.actions, isInlineLowerActions && styles.actionsInline]}>
            {lowerActions.map((action, index) => {
              const button = (
                <Button
                  key={index}
                  text={action.title}
                  prependIcon={action.appendIcon ? undefined : action.icon}
                  appendIcon={action.appendIcon}
                  variant={action.variant ? action.variant : action.primary ? 'primary' : action.danger ? 'danger' : 'secondary'}
                  size={isXS ? 'large' : 'medium'}
                  onPress={action.onPress}
                  isFullWidth={!isNative}
                  style={action.style}
                  textStyle={action.textStyle}
                  isDisabled={action.disabled}
                  isLoading={action.isLoading}
                />
              );
              // A full-width Button uses flex:1. In a column that stretches its
              // height and leaves later actions overlapping or outside the footer.
              return isNative ? (
                <View key={index} style={[styles.action, isInlineLowerActions && styles.actionInline]}>
                  {button}
                </View>
              ) : button;
            })}
          </View>
        )}
    </>
  );

  return (
    <View style={[styles.container, containerStyle]}>
      {content}
    </View>
  );
}
